import clsx from 'clsx';
import { motion } from 'framer-motion';

export function RitualPanel({
  eyebrow,
  title,
  description,
  children,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'relative overflow-hidden rounded-2xl border border-border-accent bg-bg-1 p-8 shadow-gold',
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full border border-border-accent opacity-40" />
      <div className="pointer-events-none absolute right-8 top-8 font-serif text-7xl text-accent opacity-10">
        微
      </div>
      {eyebrow && <p className="text-xs tracking-[0.3em] text-accent">{eyebrow}</p>}
      <h2 className="mt-2 font-serif text-2xl text-fg">{title}</h2>
      {description && (
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-7 text-fg-2">{description}</p>
      )}
      {children && <div className="mt-6">{children}</div>}
    </section>
  );
}

export function BreathingRing({ value, total }: { value: number; total: number }) {
  const done = value <= 0;
  return (
    <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-full border border-border-accent bg-accent-soft">
      <motion.div
        className="flex h-28 w-28 items-center justify-center rounded-full border border-accent text-center"
        animate={{ scale: done ? 1 : [1, 1.06, 1], opacity: done ? 1 : [0.8, 1, 0.8] }}
        transition={{ duration: 1.6, repeat: done ? 0 : Infinity }}
      >
        <div>
          <p className="font-serif text-3xl text-accent">{done ? '静' : value}</p>
          <p className="mt-1 text-xs text-fg-muted">{total} 秒静心</p>
        </div>
      </motion.div>
    </div>
  );
}

export function Timeline({
  items,
  activeIndex,
  onSelect,
}: {
  items: Array<{ title: string; subtitle?: string }>;
  activeIndex: number;
  onSelect?: (index: number) => void;
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max items-stretch gap-3">
        {items.map((item, index) => (
          <button
            key={`${item.title}-${index}`}
            type="button"
            onClick={() => onSelect?.(index)}
            className={clsx(
              'w-36 rounded-xl border px-4 py-3 text-left transition-all duration-fast',
              index === activeIndex
                ? 'border-accent bg-accent-soft text-accent shadow-gold'
                : 'border-border bg-bg-1 text-fg-1 hover:border-accent',
            )}
          >
            <span className="block font-serif text-lg">{item.title}</span>
            {item.subtitle && (
              <span className="mt-1 block text-xs text-fg-muted">{item.subtitle}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdvisorShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-bg-1 shadow-md">
      <div className="border-b border-border px-5 py-4">
        <p className="font-serif text-lg text-accent">观微先生 · 私人顾问室</p>
        <p className="mt-1 text-xs text-fg-muted">提问会被整理为咨询纪要，而不是普通聊天记录</p>
      </div>
      {children}
    </div>
  );
}

export function SuggestionChips({
  suggestions,
  onPick,
}: {
  suggestions: string[];
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => onPick(item)}
          className="rounded-full border border-border bg-bg-1 px-3 py-1.5 text-xs text-fg-2 transition-colors duration-fast hover:border-accent hover:text-accent"
        >
          {item}
        </button>
      ))}
    </div>
  );
}
